import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Wifi, WifiOff } from 'lucide-react';

interface ApiStatusNotificationProps {
  className?: string;
}

const ApiStatusNotification: React.FC<ApiStatusNotificationProps> = ({ className = '' }) => {
  const [apiStatus, setApiStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    let mounted = true;
    
    const checkApiStatus = async () => {
      try {
        // Use environment variable for API base URL, fallback to local development
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api/v1';
        const healthUrl = apiBaseUrl.replace('/api/v1', '/health');

        const response = await fetch(healthUrl, {
          method: 'GET',
          timeout: 5000
        } as RequestInit);
        
        if (mounted) {
          if (response.ok) {
            setApiStatus('online');
            setShowNotification(false);
          } else {
            setApiStatus('offline');
            setShowNotification(true);
          }
        }
      } catch (error) {
        if (mounted) {
          setApiStatus('offline');
          setShowNotification(true);
        }
      }
    };

    // Initial check
    checkApiStatus();

    // Check every 30 seconds
    const interval = setInterval(checkApiStatus, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  if (!showNotification) {
    return null;
  }

  return (
    <div className={`fixed top-4 right-4 z-50 ${className}`}>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 shadow-lg max-w-sm">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {apiStatus === 'checking' ? (
              <Wifi className="h-5 w-5 text-yellow-400 animate-pulse" />
            ) : apiStatus === 'online' ? (
              <CheckCircle className="h-5 w-5 text-green-400" />
            ) : (
              <WifiOff className="h-5 w-5 text-yellow-400" />
            )}
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              {apiStatus === 'checking' ? 'Checking API...' : 
               apiStatus === 'online' ? 'API Connected' : 'API Offline'}
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              {apiStatus === 'offline' && (
                <>
                  <p>Backend API is not available. The application is running in demo mode with sample data.</p>
                  <div className="mt-2">
                    <button
                      onClick={() => setShowNotification(false)}
                      className="text-yellow-800 hover:text-yellow-900 font-medium"
                    >
                      Dismiss
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiStatusNotification;
